<template>
    <div class="modal-referral-create">
        <div class="modal-referral-create__content">
            <template v-if="success">
                <div class="modal-referral-create__title">
                    Вы успешно зарегистрировались <br />в реферальной программе!
                </div>
                <div class="modal-referral-create__disclaimer">
                    <page-cabinet-referral-info-link />
                </div>
                <div class="modal-referral-create__buttons">
                    <div class="modal-referral-create__buttons-item">
                        <ui-button
                            v-on:click="close"
                            theme="primary"
                        >
                            Закрыть
                        </ui-button>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="modal-referral-create__title">
                    Регистрация в реферальной программе
                </div>
                <div class="modal-referral-create__disclaimer">
                    Для получения выплат по реферальной программе введите реквизиты ниже.
                    <br />
                    <common-document-link
                        v-bind:href="referralAgencyAgreement"
                        title="Агентский договор"
                        style="margin-top: 12px;"
                    />
                </div>
                <div class="modal-referral-create__form">
                    <div class="modal-referral-create__form-fields">
                        <ui-form
                            v-bind:model="fields"
                            v-bind:validation="$v"
                            v-bind:submit-handler="submitHandler"
                            v-on:update="updateFormData"
                        />
                    </div>
                </div>
                <div class="modal-referral-create__disclaimer">
                    Подписывая документы, вы&nbsp;подтверждаете, что&nbsp;ознакомились и&nbsp;согласны с&nbsp;условиями Агентского договора.
                    <br />
                    Подписание осуществляется с&nbsp;использованием простой электронной подписи (ПЭП).
                </div>
                <div class="modal-referral-create__error"
                     v-bind:class="{ _active: error }"
                >
                    <transition name="fade">
                        <span class="modal-referral-create__error-text"
                              v-if="error"
                              v-text="error"
                        />
                    </transition>
                </div>
                <div v-if="isShowSmsConfirmation">
                    <common-sms-verification
                        v-bind:phone="user.phone"
                        v-on:submit="setCode"
                        verification-key="document"
                        purpose-text="регистрации в реферальной программе"
                        is-request-required
                    />
                </div>
                <div class="modal-referral-create__buttons" v-if="!isShowSmsConfirmation">
                    <div class="modal-referral-create__buttons-item">
                        <ui-button
                            v-bind:disabled="$v.$dirty && $v.$invalid"
                            v-on:click="submitHandler"
                            theme="primary"
                            v-bind:is-loading="isLoading"
                        >
                            Подписать
                        </ui-button>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import forms from '~/forms';
import config from '~/config';

export default {
    name: 'modal-referral-create',
    props: {
        onClose: {
            type: Function,
        },
    },
    data: () => ({
        success: false,
        fields: {
            referralBankAccount: { ...forms.fields.user.juridical.referralBankAccount },
            juridicalBik: { ...forms.fields.user.juridical.juridicalBik },
            snils: { ...forms.fields.referralSnils },
        },
        smsCode: null,
        isShowSmsConfirmation: false,
    }),
    validations() {
        return {
            fields: {
                referralBankAccount: { ...forms.validations.referralBankAccount },
                juridicalBik: { ...forms.validations.bik },
                snils: { ...forms.validations.snils },
            },
        };
    },
    computed: {
        ...mapState('referral', [
            'createReferrerIsLoading',
            'createReferrerResult',
            'createReferrerError',
        ]),
        ...mapState('users', [
            'getUserJuridicalInfoIsLoading',
            'getUserJuridicalInfoResult',
        ]),
        isLoading() {
            return this.createReferrerIsLoading || this.getUserJuridicalInfoIsLoading;
        },
        user() {
            return this.$store.getters['users/user'];
        },
        error() {
            if (this.createReferrerError) {
                return 'Не удалось зарегистрироваться в реферальной программе. Попробуйте позже';
            }
            return null;
        },
        smsVerificationProps() {
            return {
                phone: this.user.phone,
                isRequestRequired: true,
            };
        },
        referralAgencyAgreement() {
            return config.urls.policies.referralAgencyAgreement;
        },
    },
    methods: {
        updateFormData(newData) {
            this.fields = newData;
        },
        close() {
            this.$store.commit('modals/pop');
        },
        setCode(code) {
            this.smsCode = code;
            const payload = {
                value: this.user.phone,
                code: this.smsCode,
                key: 'document',
                bik: this.fields.juridicalBik.value,
                // eslint-disable-next-line
                bank_account: this.fields.referralBankAccount.value,
                inn: this.getUserJuridicalInfoResult?.inn || '',
                snils: this.fields.snils.value,
            };
            this.$store.dispatch('referral/createReferrer', {
                ...payload,
                onSuccess: () => {
                    this.success = true;
                    this.$store.dispatch('users/getUser');
                    this.$store.dispatch('referral/getReferrals');
                },
            });
        },
        submitHandler() {
            if (this.$v.$invalid) {
                this.$v.$touch();
                return;
            }
            if (this.smsCode === null) {
                this.isShowSmsConfirmation = true;
            }
        },
    },
    beforeMount() {
        this.isShowSmsConfirmation = false;
    },
    beforeDestroy() {
        if (this.onClose && typeof this.onClose === 'function') {
            this.onClose();
        }
    },
};
</script>

<style scoped lang="less">
@import '~theme';

.modal-referral-create {
    .modal();

    width: 100%;
    max-width: 900px;

    &__content {
        display: flex;
        flex-direction: column;
        gap: 32px;
        padding: 16px 0 35px;
    }
    &__success {
        padding: 0 0 80px;

        text-align: center;
        &-title {
            .typography-large();
        }
        &-subtitle {
            .typography-body();
        }
    }
    &__title {
        .typography-large();
    }
    &__disclaimer {
        .typography-body();
    }
    &__form {
        .typography-body();

        max-width: 420px;
    }
    &__error {
        .typography-body();

        &-text {
            .transition-fade();

            color: @color-accent-negative;
        }
    }
    &__buttons {
        display: flex;
        align-items: center;
        gap: 12px;
        &-item {
            display: block;
        }
    }
    @media @media-md-down {
        &__title {
            .typography-medium();
        }
        &__buttons {
            align-items: center;
            justify-content: center;
        }
    }
    @media @media-sm-down {
        padding: 0;

        &__success {
            padding: 40px 16px;
        }
        &__content {
            padding: 12px 16px 35px;
            gap: 16px;
        }
    }
    @media @media-xs-down {

    }
}
</style>
